<template>
  <div class="asc-plot-to-issues-grid">
    <asc-ag-grid-filter
      :key="`asc-plot-to-issues-grid_${gridId}`"
      :grid-options="agGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
      :selections="selectedIssues"
      @onSelectionChanged="handleSelectionChanged"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscAgGridAutocompleteFloatFilter,
  AscAgGridDateFloatFilter,
  AscTooltipCellRenderer,
} from '@/components/agGrid'
import {
  exactFilterToValueMatcher,
  dateStringMatcher,
} from '@/components/agGrid/AgGridHelperMethods'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import { getFormattedDateTime } from '@/utilities/dates'

export default {
  name: 'AscPlotToIssuesGrid',

  components: {
    AscAgGridFilter,
    AscAgGridAutocompleteFloatFilter,
    AscAgGridDateFloatFilter,
    AscTooltipCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    onSelectionChanged: {
      type: Function,
      default: () => {},
    },
    selectedIssues: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      localStorageName: 'maintenance_grid_read_ids',
      updateDateFieldName: 'lastAlertDate',
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        rowSelection: 'multiple',
        getRowId: params => params.data.issueId,
        rowMultiSelectWithClick: true,
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
      }),
    }
  },

  computed: {
    mappedValues() {
      return this.values.filter(row => ![9, 10].includes(row.sourceId))
    },
    agGridRowData() {
      this.resetLocalStorage(this.localStorageName)
      return this.mappedValues
    },
    getGridColumnDefs() {
      return [
        {
          headerName: this.getColumnTitle(this.gridColumns, 'id', 'ID'),
          field: 'id',
          cellRendererParams: {
            localStorageName: this.localStorageName,
            updateDateFieldName: this.updateDateFieldName,
          },
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'analyticModule',
            'Analytic',
          ),
          field: 'analyticModule',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: exactFilterToValueMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('analyticModule'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'createdDate',
            'Date Created',
          ),
          field: 'createdDate',
          width: 155,
          minWidth: 155,
          sort: 'desc',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: dateStringMatcher,
          },
          floatingFilterComponent: 'AscAgGridDateFloatFilter',
          floatingFilterComponentParams: {
            suppressFilterButton: true,
            items: this.values,
            dateFloatFilterConfig: {
              left: true,
              transitionOrigin: 'top right',
            },
          },
          valueFormatter: data => {
            return data.value ? getFormattedDateTime(data.value) : null
          },
          comparator: this.sortDate,
        },
      ]
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  methods: {
    handleSelectionChanged(rows) {
      if (this.onSelectionChanged) {
        this.onSelectionChanged(rows.map(row => row.id))
      }
    },
  },
}
</script>

<style lang="scss">
.asc-plot-to-issues-grid {
  max-height: 650px;
  overflow-y: hidden;

  .ag-theme-balham {
    .ag-row {
      &.ag-row-selected {
        background-color: $accent-100;
      }

      &.ag-row-hover {
        cursor: pointer;

        &:not(.ag-row-selected) {
          background-color: $accent-0;
        }
      }
    }
  }
}
</style>
